import api from '../config/api';
import { formatDateDisplay } from './formatDate';

export function getItemDisplayName(item) {
  if (item.item_type === 'Cakes' && item.product_name) {
    return item.flavour ? `${item.product_name} (${item.flavour})` : item.product_name;
  }
  if (item.item_type === 'Other Items' && item.custom_item_name) return item.custom_item_name;
  if (item.item_type === 'Desserts' && item.dessert_name) return item.dessert_name;
  if (item.item_type === 'Cup Cakes' && item.cup_cake_name) return item.cup_cake_name;
  if (item.item_type === 'Plants' && item.plant_name) return item.plant_name;
  if (item.item_type === 'Snack Boxes' && item.snack_box_items?.length) {
    const names = item.snack_box_items.map((s) => `${s.item_name} x${s.quantity}`).join(', ');
    return `Snack Box: ${names}`;
  }
  return item.item_type;
}

/** Plain-text fallback when the browser cannot share the PDF file */
export function buildKitchenShareText(order) {
  const lines = [
    `Kitchen copy – CakeXpress`,
    `Order ID: ${order.order_number}`,
    `Delivery Date: ${formatDateDisplay(order.delivery_date)}`,
    `Delivery Time: ${order.delivery_time || '—'}`,
    ''
  ];
  if (order.items?.length) {
    order.items.forEach((it, i) => {
      if (it.item_type === 'Cakes') {
        lines.push(`Cake ${i + 1}`);
        lines.push(`Name: ${it.product_name || '—'}`);
        lines.push(`Flavour: ${it.flavour || '—'}`);
        const totalKg = Number(it.weight_kg || 0) + parseFloat(it.weight_decimal || 0);
        lines.push(`Weight: ${totalKg > 0 ? `${Number(totalKg.toFixed(2))} KG` : '—'}`);
        if (it.message_on_cake) lines.push(`Message on cake: ${it.message_on_cake}`);
        if (it.message_on_card) lines.push(`Message on card: ${it.message_on_card}`);
        if (it.message_on_base) lines.push(`Message on base: ${it.message_on_base}`);
        if (Array.isArray(it.cake_other_items) && it.cake_other_items.length) {
          it.cake_other_items.forEach((oi) => {
            lines.push(
              `Other item: ${oi.item_name} × ${oi.quantity != null ? oi.quantity : 1} · ₹${oi.unit_cost != null ? oi.unit_cost : 0}`
            );
          });
        }
        const special = [it.cake_instructions, it.special_instructions].filter(Boolean).join(' · ');
        if (special) lines.push(`Special instructions: ${special}`);
        lines.push('');
      } else {
        lines.push(`${i + 1}. ${getItemDisplayName(it)} × ${it.quantity != null ? it.quantity : 1}`);
      }
    });
  }
  return lines.join('\n').trim();
}

/**
 * Fetch kitchen PDF and share via Web Share API (mobile) or WhatsApp text link (fallback).
 * @param {object} order – enough fields for buildKitchenShareText (list or detail payload)
 * @param {string|number} orderId
 */
export async function shareKitchenCopyWhatsApp(order, orderId) {
  if (!order || orderId == null) return;

  let blob;
  try {
    const response = await api.get(`/pdf/kitchen-copy/${orderId}`, {
      responseType: 'blob'
    });
    blob = response.data;
  } catch (error) {
    console.error('Kitchen copy fetch:', error);
    const msg = error.response?.data?.error || error.message || 'Error generating kitchen copy';
    alert(typeof msg === 'string' ? msg : 'Error generating kitchen copy');
    return;
  }

  const filename = `kitchen-${order.order_number || orderId}.pdf`;
  const file = new File([blob], filename, { type: 'application/pdf' });
  const shortCaption = `Kitchen copy – ${order.order_number} · ${order.customer_name}`;

  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: 'Kitchen copy',
        text: shortCaption
      });
      return;
    }
  } catch (e) {
    if (e.name === 'AbortError') return;
  }

  try {
    if (navigator.share) {
      await navigator.share({
        title: 'Kitchen copy',
        text: buildKitchenShareText(order)
      });
      return;
    }
  } catch (e) {
    if (e.name === 'AbortError') return;
  }

  const url = `https://wa.me/?text=${encodeURIComponent(buildKitchenShareText(order))}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}
